import { ensureArray } from '@epicuro-next/utilities';
import { Actions } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  BehaviorSubject,
  concatMap,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import {
  DynamicConditionalActionChainArgs,
  isDynamicConditionalActionChainArgs,
  isStep,
  SelectNextByActionStep,
  SelectNextByValueStep,
  StepNextType,
} from './types';

function SelectNextByValueStepTypeHandler(
  step: SelectNextByValueStep<unknown>,
): Observable<StepNextType | undefined> {
  return step.value.pipe(
    map((value) => step.nextSelector.find((n) => n.matcher(value))),
    filter((v) => v !== undefined),
    take(1),
    // tslint:disable-next-line:no-non-null-assertion
    map((v) => v!),
    map(({ nextStep }) => {
      return nextStep;
    }),
  );
}

function SelectNextByActionStepTypeHandler(
  step: SelectNextByActionStep,
  actions$: Actions<Action>,
  store: Store,
): Observable<StepNextType | undefined> {
  const onAction$ = new BehaviorSubject<StepNextType | undefined>(undefined);

  actions$
    .pipe(
      map((action) => {
        const match = step.next.find((n) => n.matcher(action));
        return match;
      }),
      filter((match) => match !== undefined),
      // tslint:disable-next-line:no-non-null-assertion
      map((match) => match!),
      take(1),
      map((match) => match.nextStep),
    )
    .subscribe((next) => {
      if (next !== undefined) {
        onAction$.next(next);
      } else {
        onAction$.complete();
      }
    });

  return of(step.dispatch).pipe(
    tap((toDispatch) => {
      if (toDispatch !== undefined) {
        const actions = ensureArray(toDispatch);
        for (const action of actions) {
          store.dispatch(action);
        }
      }
    }),
    mergeMap(() => {
      return onAction$.pipe(
        filter((v) => v !== undefined),
        take(1),
      );
    }),
  );
}

export function ConditionalActionFlowStepExecuter(
  step: DynamicConditionalActionChainArgs['steps'][0],
  store: Store,
  actions$: Actions<Action>,
): Observable<Action[]> {
  if (isStep(step)) {
    let next$: Observable<StepNextType | undefined> | undefined;

    switch (step.stepType) {
      case 'SelectNextByValueStep':
        next$ = SelectNextByValueStepTypeHandler(step);
        break;
      case 'SelectNextByActionStep':
        next$ = SelectNextByActionStepTypeHandler(step, actions$, store);
        break;
    }

    if (next$ === undefined) {
      throw new Error(`No next found for step: '${step.stepType}'.`);
    }

    return next$.pipe(
      switchMap((next) => {
        if (next === undefined) {
          throw new Error(`No next found for step: '${step.stepType}'.`);
        }

        if (isDynamicConditionalActionChainArgs(next)) {
          return ConditionalActionFlowExecuter(next, store, actions$);
        } else {
          const actions = ensureArray(next);
          return of(actions);
        }
      }),
    );
  } else {
    const actions = ensureArray(step);
    return of(actions);
  }
}

export function ConditionalActionFlowExecuter(
  args: DynamicConditionalActionChainArgs,
  store: Store,
  actions$: Actions<Action>,
): Observable<Action[]> {
  return of(args).pipe(
    concatMap((flowArgs) => {
      return of(...flowArgs.steps);
    }),
    concatMap((step) => {
      return ConditionalActionFlowStepExecuter(step, store, actions$);
    }),
  );
}
